<template>
  <div :class="{ 'agent-select-wrapper': iconMode && !multi }">
    <!-- Icon mode -->
    <template v-if="iconMode && !multi">
      <v-menu
        v-model="iconModeMenuOpen"
        :close-on-content-click="false"
        location="bottom end"
      >
        <template v-slot:activator="{ props }">
          <!-- No agent selected -->
          <v-tooltip
            v-if="!selectedValues"
            :text="iconModeTooltip || placeholder || 'Select Agent'"
            location="top"
          >
            <template v-slot:activator="{ props: tooltipProps }">
              <v-btn
                v-bind="{ ...props, ...tooltipProps }"
                icon="mdi-account-plus"
                size="small"
                variant="text"
              ></v-btn>
            </template>
          </v-tooltip>
          <!-- show profile pic and name in pill -->
          <v-chip
            v-else
            v-bind="props"
            class="agent-select-pill"
            variant="outlined"
            size="small"
            :height="32"
          >
            <v-avatar start size="28">
              <v-img
                v-if="
                  selectedAgentData &&
                  selectedAgentData.photo_asset_id
                "
                :src="getPhotoUrl(selectedAgentData.photo_asset_id)"
                :alt="`${selectedAgentData.first_name} ${selectedAgentData.last_name}`"
                cover
              >
                <template v-slot:error>
                  <v-icon size="16">mdi-account</v-icon>
                </template>
              </v-img>
              <v-icon v-else size="16">mdi-account</v-icon>
            </v-avatar>
            {{
              selectedAgentData
                ? `${selectedAgentData.first_name} ${selectedAgentData.last_name}`
                : ''
            }}
          </v-chip>
        </template>
        <v-list density="compact">
          <div class="px-3 py-2">
            <v-text-field
              v-model="iconModeSearch"
              placeholder="Search agents..."
              density="compact"
              variant="outlined"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              @input="filterIconModeAgents"
            ></v-text-field>
          </div>
          <v-divider></v-divider>

          <v-list-item
            v-if="
              allowAll &&
              (!iconModeSearch || 'all'.includes(iconModeSearch.toLowerCase()))
            "
            :value="0"
            @click="selectAgentFromMenu(0)"
          >
            <v-list-item-title>{{
              allLabel || 'All Agents'
            }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-for="agent in filteredIconModeAgents"
            :key="agent.id"
            :value="agent.id"
            @click="selectAgentFromMenu(agent.id)"
          >
            <template v-slot:prepend>
              <v-avatar size="32">
                <v-img
                  v-if="agent.photo_asset_id"
                  :src="getPhotoUrl(agent.photo_asset_id)"
                  :alt="`${agent.first_name} ${agent.last_name}`"
                  cover
                ></v-img>
                <v-icon v-else size="20">mdi-account</v-icon>
              </v-avatar>
            </template>
            <v-list-item-title
              >{{ agent.first_name }} {{ agent.last_name }}</v-list-item-title
            >
          </v-list-item>
          <v-list-item
            v-if="filteredIconModeAgents.length === 0 && iconModeSearch"
          >
            <v-list-item-title class="text-grey text-center">
              No agents found
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
    <template v-else-if="autocomplete">
      <!-- Single select autocomplete -->
      <div v-if="!multi" class="typeahead-container">
        <v-text-field
          v-model="searchQuery"
          :label="placeholder || 'Type Agent Name'"
          @input="onSearchInput"
          :density="vuetifyDensity"
          variant="outlined"
          :block="block"
          :loading="loading"
          clearable
          @click:clear="doClear"
        >
          <template v-slot:append-inner v-if="loading">
            <v-progress-circular
              indeterminate
              color="primary"
              size="20"
            ></v-progress-circular>
          </template>
        </v-text-field>

        <!-- Results dropdown for autocomplete -->
        <div
          v-if="showResults && filteredAgents.length > 0"
          class="results-dropdown"
        >
          <div
            v-for="agent in filteredAgents"
            :key="agent.id"
            class="result-item"
            @click="selectAgent(agent)"
          >
            {{ agent.first_name }} {{ agent.last_name }}
          </div>
        </div>
      </div>
      <!-- Multi select with No User option and search -->
      <div v-else-if="allowNone" class="typeahead-container">
        <v-menu
          v-model="noUserMenuOpen"
          :close-on-content-click="false"
          max-height="400"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-bind="props"
              :label="label || 'Select Agents'"
              :placeholder="placeholder"
              variant="outlined"
              :density="vuetifyDensity"
              readonly
              hide-details="auto"
              :style="getSelectStyle()"
            >
              <template v-slot:prepend-inner>
                <div class="d-flex flex-wrap align-center ga-1">
                  <template v-if="isNoUserSelected">
                    <v-chip
                      size="small"
                      closable
                      color="grey"
                      @click:close="removeNoUser"
                    >
                      {{ noneLabel }}
                    </v-chip>
                  </template>
                  <template v-else>
                    <v-chip
                      v-for="agentId in displayedSelectedAgents"
                      :key="agentId"
                      size="small"
                      closable
                      color="primary"
                      @click:close="removeAgentFromSelection(agentId)"
                    >
                      {{ getAgentDisplayName(agentId) }}
                    </v-chip>
                    <v-chip
                      v-if="remainingAgentsCount > 0"
                      size="small"
                      color="grey"
                    >
                      +{{ remainingAgentsCount }} more
                    </v-chip>
                  </template>
                </div>
              </template>
              <template v-slot:append-inner>
                <v-icon>mdi-chevron-down</v-icon>
              </template>
            </v-text-field>
          </template>
          <v-card min-width="300">
            <v-text-field
              v-model="agentSearchQuery"
              placeholder="Search agents..."
              density="compact"
              variant="outlined"
              hide-details
              class="ma-2"
              prepend-inner-icon="mdi-magnify"
              clearable
            ></v-text-field>
            <v-list density="compact" max-height="300" class="overflow-y-auto">
              <v-list-item @click="toggleNoUser">
                <template v-slot:prepend>
                  <v-checkbox-btn
                    :model-value="isNoUserSelected"
                    color="primary"
                  />
                </template>
                <v-list-item-title>{{ noneLabel }}</v-list-item-title>
              </v-list-item>
              <v-divider class="my-1" />
              <v-list-item
                v-for="agent in filteredAgentList"
                :key="agent.id"
                @click="toggleAgentSelection(agent.id)"
              >
                <template v-slot:prepend>
                  <v-checkbox-btn
                    :model-value="isAgentInSelection(agent.id)"
                    color="primary"
                  />
                </template>
                <v-list-item-title>{{ agent.first_name }} {{ agent.last_name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>
      <!-- Multi select without No User option -->
      <div v-else class="typeahead-container">
        <v-autocomplete
          v-model="selectedValues"
          @update:model-value="doChange"
          @click:clear="doClear"
          :label="label || 'Select Agents'"
          :placeholder="placeholder || 'Type Agent Name'"
          :items="selectOptions"
          item-title="title"
          item-value="value"
          multiple
          chips
          closable-chips
          :density="vuetifyDensity"
          variant="outlined"
          :style="getSelectStyle()"
          hide-details="auto"
          :clearable="clearable"
          :loading="loading"
        ></v-autocomplete>
      </div>
    </template>
    <template v-else>
      <!-- Custom multi-select with No User option -->
      <v-menu
        v-if="showNoUserOption"
        v-model="noUserMenuOpen"
        :close-on-content-click="false"
        max-height="400"
      >
        <template v-slot:activator="{ props }">
          <v-text-field
            v-bind="props"
            :label="label || 'Select Agents'"
            :placeholder="placeholder"
            variant="outlined"
            :density="vuetifyDensity"
            readonly
            hide-details="auto"
            :style="getSelectStyle()"
          >
            <template v-slot:prepend-inner>
              <div class="d-flex flex-wrap align-center ga-1">
                <template v-if="isNoUserSelected">
                  <v-chip
                    size="small"
                    closable
                    color="grey"
                    @click:close="removeNoUser"
                  >
                    {{ noneLabel }}
                  </v-chip>
                </template>
                <template v-else>
                  <v-chip
                    v-for="agentId in displayedSelectedAgents"
                    :key="agentId"
                    size="small"
                    closable
                    color="primary"
                    @click:close="removeAgentFromSelection(agentId)"
                  >
                    {{ getAgentDisplayName(agentId) }}
                  </v-chip>
                  <v-chip
                    v-if="remainingAgentsCount > 0"
                    size="small"
                    color="grey"
                  >
                    +{{ remainingAgentsCount }} more
                  </v-chip>
                </template>
              </div>
            </template>
            <template v-slot:append-inner>
              <v-icon>mdi-chevron-down</v-icon>
            </template>
          </v-text-field>
        </template>
        <v-card min-width="300">
          <v-text-field
            v-model="agentSearchQuery"
            placeholder="Search agents..."
            density="compact"
            variant="outlined"
            hide-details
            class="ma-2"
            prepend-inner-icon="mdi-magnify"
            clearable
          ></v-text-field>
          <v-list density="compact" max-height="300" class="overflow-y-auto">
            <v-list-item @click="toggleNoUser">
              <template v-slot:prepend>
                <v-checkbox-btn
                  :model-value="isNoUserSelected"
                  color="primary"
                />
              </template>
              <v-list-item-title>{{ noneLabel }}</v-list-item-title>
            </v-list-item>
            <v-divider class="my-1" />
            <v-list-item
              v-for="agent in filteredAgentList"
              :key="agent.id"
              @click="toggleAgentSelection(agent.id)"
            >
              <template v-slot:prepend>
                <v-checkbox-btn
                  :model-value="isAgentInSelection(agent.id)"
                  color="primary"
                />
              </template>
              <v-list-item-title>{{ agent.first_name }} {{ agent.last_name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
      <v-select
        v-else
        v-model="selectedValues"
        @update:model-value="doChange"
        @click:clear="doClear"
        :label="label || (multi ? 'Select Agents' : 'Select an Agent')"
        :items="selectOptions"
        :multiple="multi"
        :density="vuetifyDensity"
        :placeholder="placeholder"
        variant="outlined"
        :style="getSelectStyle()"
        hide-details="auto"
        :clearable="clearable"
      >
        <template v-if="multi" v-slot:selection="{ item, index }">
          <v-chip v-if="index < maxVisibleSelections" size="small" class="mr-1">
            {{ item.title }}
          </v-chip>
          <span
            v-else-if="index === maxVisibleSelections"
            class="text-caption text-grey-darken-1"
          >
            +{{ selectedAgentIds.length - maxVisibleSelections }} more
          </span>
        </template>
      </v-select>
    </template>
  </div>
</template>

<script>
import { isProxy, toRaw } from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'agentSelect',
  computed: {
    ...mapGetters('agents', ['getAgent', 'getAgents']),
    ...mapGetters('userPhotos', ['getPhotoUrl']),
    selectedAgentData() {
      if (!this.selectedValues || this.selectedValues === 0) {
        return null;
      }
      return this.getAgents.find((agent) => agent.id === this.selectedValues);
    },
    filteredIconModeAgents() {
      if (!this.iconModeSearch) {
        return this.getAgents || [];
      }

      const search = this.iconModeSearch.toLowerCase();
      return (this.getAgents || []).filter((agent) => {
        const fullName = `${agent.first_name} ${agent.last_name}`.toLowerCase();
        return fullName.includes(search);
      });
    },
    filteredAgentList() {
      if (!this.agentSearchQuery) {
        return this.getAgents || [];
      }

      const search = this.agentSearchQuery.toLowerCase();
      return (this.getAgents || []).filter((agent) => {
        const fullName = `${agent.first_name} ${agent.last_name}`.toLowerCase();
        return fullName.includes(search);
      });
    },
    showNoUserOption() {
      return this.allowNone && this.multi;
    },
    isNoUserSelected() {
      return this.noUserSelected;
    },
    selectOptions() {
      const options = [];
      if (this.allowAll) {
        options.push({
          value: 0,
          title: this.allLabel ? this.allLabel : 'All Agents',
        });
      }
      if (this.getAgents && this.getAgents.length > 0) {
        this.getAgents.forEach((agent) => {
          options.push({
            value: agent.id,
            title: `${agent.first_name} ${agent.last_name}`,
          });
        });
      }
      return options;
    },
    selectedAgentIds() {
      if (!Array.isArray(this.selectedValues)) return [];
      return this.selectedValues;
    },
    displayedSelectedAgents() {
      const agents = this.selectedAgentIds;
      return agents.slice(0, this.maxVisibleSelections);
    },
    remainingAgentsCount() {
      return Math.max(0, this.selectedAgentIds.length - this.maxVisibleSelections);
    },
    vuetifyDensity() {
      if (this.density) {
        return this.density;
      }
      const densityMap = {
        default: 'default',
        small: 'compact',
        large: 'comfortable',
      };
      return densityMap[this.size] || 'compact';
    },
  },
  props: {
    iconMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    iconModeTooltip: {
      type: String,
      required: false,
    },
    values: {
      type: Array,
      required: true,
    },
    multi: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    allowAll: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    allLabel: {
      type: String,
      required: false,
    },
    allowNone: {
      type: Boolean,
      required: false,
      default: false,
    },
    noneSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    noneLabel: {
      type: String,
      required: false,
      default: 'No User',
    },
    size: {
      type: String,
      required: false,
      default: () => {
        return 'small';
      },
    },
    density: {
      type: String,
      required: false,
      validator: (value) =>
        ['comfortable', 'default', 'compact'].includes(value),
    },
    autocomplete: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    placeholder: {
      type: String,
      required: false,
    },
    block: {
      type: Boolean,
      required: false,
    },
    label: {
      type: String,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    width: {
      type: String,
      required: false,
      default: () => {
        return '100%';
      },
    },
    display: {
      type: String,
      required: false,
      default: () => {
        return 'block';
      },
    },
  },
  emits: ['update:values', 'change'],
  data() {
    return {
      selectedValues: [],
      searchQuery: '',
      showResults: false,
      filteredAgents: [],
      loading: false,
      debounceTimeout: null,
      selectedAgent: null,
      maxVisibleSelections: 3,
      iconModeSearch: '',
      iconModeMenuOpen: false,
      noUserMenuOpen: false,
      noUserSelected: false,
      agentSearchQuery: '',
    };
  },
  async created() {
    if (!this.multi) {
      this.selectedValues = undefined;
    }
    if (!this.getAgents || this.getAgents.length == 0) {
      this.loading = true;
      await this.fetchAgents(0);
      this.loading = false;
    }
    if (this.values) {
      if (!this.multi) {
        this.selectedValues = this.values.length ? this.values[0] : undefined;

        if (this.autocomplete && this.values.length) {
          const agent = this.getAgents.find((a) => a.id === this.values[0]);
          if (agent) {
            this.searchQuery = `${agent.first_name} ${agent.last_name}`;
            this.selectedAgent = agent;
          }
        }
      } else {
        this.selectedValues = this.values.slice();
      }
    }
  },
  watch: {
    noneSelected: {
      immediate: true,
      handler(val) {
        this.noUserSelected = val;
      },
    },
    values: {
      immediate: true,
      handler(n) {
        if (!this.multi) {
          if (isProxy(n)) {
            const vals = toRaw(n);
            this.selectedValues = vals.length ? vals[0] : undefined;
          } else {
            this.selectedValues = n.length ? n[0] : undefined;

            const agent = toRaw(this.getAgents).find(({ id }) => id == n[0]);
            if (agent && this.autocomplete) {
              this.searchQuery = `${agent.first_name} ${agent.last_name}`;
              this.selectedAgent = agent;
            }
          }
        } else {
          this.selectedValues = Array.isArray(n) ? n : [];
        }
      },
    },
  },
  mounted() {
    if (this.autocomplete) {
      document.addEventListener('click', this.handleClickOutside);
    }
  },
  beforeUnmount() {
    if (this.autocomplete) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  },
  methods: {
    ...mapActions('agents', ['fetchAgents']),
    doClear() {
      this.selectedValues = this.multi ? [] : undefined;
      this.searchQuery = '';
      this.selectedAgent = null;
      this.doChange();
    },
    doChange() {
      if (!this.multi) {
        this.$emit(
          'update:values',
          this.selectedValues ? [this.selectedValues] : []
        );
        this.$emit('change', this.selectedValues ? [this.selectedValues] : []);
      } else {
        this.$emit('update:values', this.selectedValues);
        this.$emit('change', this.selectedValues);
      }
    },
    handleClickOutside(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.showResults = false;
      }
    },
    onSearchInput() {
      this.showResults = true;

      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      if (this.selectedAgent) {
        const fullName =
          this.selectedAgent.first_name + ' ' + this.selectedAgent.last_name;
        if (this.searchQuery !== fullName) {
          this.selectedAgent = null;
          this.selectedValues = undefined;
          this.$emit('update:values', []);
          this.$emit('change', []);
        }
      }

      this.debounceTimeout = setTimeout(() => {
        this.filterAgents(this.searchQuery);
      }, 300);
    },
    filterAgents(input) {
      if (!input || input.length < 1) {
        this.filteredAgents = [];
        this.loading = false;
        return;
      }

      this.loading = true;

      const query = input.toLowerCase();
      this.filteredAgents = this.getAgents.filter(
        (agent) =>
          agent.first_name.toLowerCase().includes(query) ||
          agent.last_name.toLowerCase().includes(query)
      );

      this.loading = false;
    },
    selectAgent(agent) {
      this.selectedAgent = agent;
      this.searchQuery = `${agent.first_name} ${agent.last_name}`;
      this.showResults = false;
      this.selectedValues = agent.id;
      this.$emit('update:values', [agent.id]);
      this.$emit('change', [agent.id]);
    },
    selectAgentFromMenu(agentId) {
      this.selectedValues = agentId === 0 ? undefined : agentId;
      this.doChange();
      this.iconModeSearch = '';
      this.iconModeMenuOpen = false;
    },
    filterIconModeAgents() {
      if (this.iconModeSearch && this.selectedValues) {
        this.selectedValues = undefined;
        this.doChange();
      }
    },
    getSelectStyle() {
      return this.block ? 'display:block' : '';
    },
    toggleNoUser() {
      if (this.noUserSelected) {
        this.noUserSelected = false;
      } else {
        this.noUserSelected = true;
        this.selectedValues = [];
      }
      this.emitNoUserChange();
    },
    removeNoUser() {
      this.noUserSelected = false;
      this.emitNoUserChange();
    },
    toggleAgentSelection(agentId) {
      if (this.noUserSelected) {
        this.noUserSelected = false;
      }

      const current = Array.isArray(this.selectedValues) ? [...this.selectedValues] : [];
      const index = current.indexOf(agentId);

      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(agentId);
      }

      this.selectedValues = current;
      this.emitNoUserChange();
    },
    removeAgentFromSelection(agentId) {
      if (!Array.isArray(this.selectedValues)) return;

      const current = [...this.selectedValues];
      const index = current.indexOf(agentId);

      if (index >= 0) {
        current.splice(index, 1);
        this.selectedValues = current;
        this.emitNoUserChange();
      }
    },
    isAgentInSelection(agentId) {
      if (!Array.isArray(this.selectedValues)) return false;
      return this.selectedValues.includes(agentId);
    },
    getAgentDisplayName(agentId) {
      const agent = this.getAgents?.find((a) => a.id === agentId);
      return agent ? `${agent.first_name} ${agent.last_name}` : 'Unknown';
    },
    emitNoUserChange() {
      if (this.noUserSelected) {
        this.$emit('update:values', null);
        this.$emit('change', null);
      } else {
        const agentIds = this.selectedAgentIds;
        this.$emit('update:values', agentIds);
        this.$emit('change', agentIds);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.ga-1 {
  gap: 4px;
}

.typeahead-container {
  position: relative;
  width: 100%;
}

.results-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 10;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: -1px;
}

.result-item {
  padding: 10px 15px;
  cursor: pointer;
  text-align: left;
}

.result-item:hover {
  background-color: #f5f7fa;
}

.agent-select-pill {
  cursor: pointer;
  font-size: 0.875rem;
  padding-right: 12px !important;
  padding-left: 2px !important;
  background-color: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.04);
  display: inline-flex;
  align-items: center;
}

.agent-select-pill:hover {
  background-color: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.08);
}

:deep(.v-chip__content) {
  display: flex;
  align-items: center;
  gap: 8px;
}

.agent-select-wrapper {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  height: 40px;
}

.agent-select-wrapper .v-btn {
  margin: 0;
}

.agent-select-wrapper .agent-select-pill {
  margin: 0;
  vertical-align: middle;
}
</style>
